import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import { LocaleContext } from "../routing/LangRouter";
import { useContext } from "react";
import { toast } from "react-toastify";
import BASE_URL, { BASE_URL_IR } from "../constants/baseUrls";
import axios from "axios";
import { useTranslation } from "react-i18next";

//import utils
import { extractErrorMessages } from "../utils/apiErrorExtractor";

const useInternationalBooking = (
    selectedIntFlight,
    updatedFlightPassengers,
    updatedHotelPassengers,
    isPaymentByCredit,
    setLoading,
    hotelInformation,
    selectedHotel,
    isHotelInternational,
) => {
    //general variables
    const navigate = useNavigate();
    const accessToken = localStorage.getItem("accessToken");
    const [_, setSearchParams] = useSearchParams();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    // Function to handle errors and display them as toast messages
    const handleError = error => {
        const errors = error?.response?.data?.errors;
        if (errors && Array.isArray(errors)) {
            const { result } = extractErrorMessages(errors);
            const stayError = result?.find(err => err?.key === "StayNotInRange");
            if (stayError) {
                toast.error(stayError?.message, { toastId: "custom-id-StayNotInRange" });
            }
        }

        if (error?.response?.status === 400) {
            toast.error(t("Toast Messages.Error Occurred"));
        } else if (error?.response?.status === 401) {
            localStorage.clear();
            setSearchParams({ action: "login" });
        } else {
            if (locale === "en") {
                toast.error("Payment encountered an error", { toastId: "custom-id-en" });
            } else {
                if (errors && Array.isArray(errors)) {
                    const { messages } = extractErrorMessages(errors);
                    messages?.forEach((message, index) => {
                        toast.error(message, { toastId: `custom-id-${index}-${Date.now()}` });
                    });
                } else {
                    toast.error("An unknown error occurred", { toastId: "custom-id-unknown" });
                }
            }
        }
    };

    // Function to handle international reservation
    const internationalBooking = async () => {
        if (!!accessToken) {
            const flight = selectedIntFlight
                ? [
                      {
                          fare_source_code: selectedIntFlight.fare_source_code,
                          adult_count: selectedIntFlight.fares.adult.count,
                          child_count: selectedIntFlight.fares.child.count,
                          infant_count: selectedIntFlight.fares.infant.count,
                          fare_type: selectedIntFlight.fares.type,
                          passengers: updatedFlightPassengers["pr"],
                      },
                  ]
                : [];

            const ch118_flight = [
                {
                    ref_number: selectedIntFlight.ref_number,
                    is_return: false,
                    aircraft: selectedIntFlight?.segments?.[0]?.aircraft,
                    destination_date: selectedIntFlight?.segments?.[0]?.arrival_date,
                    flight_duration: selectedIntFlight?.segments?.[0]?.flight_duration,
                    cobin: selectedIntFlight.cabin.english,
                    cobin_persian: selectedIntFlight.cabin.persian,
                    type: selectedIntFlight?.type,
                    baggage: selectedIntFlight?.segments?.[0]?.baggage,
                    passengers: updatedFlightPassengers["ch118"],
                },
            ];
            let domestic_pr_accommodation = [];
            let domestic_grs_accommodation = [];

            const requestData = {
                ...(!!isHotelInternational && {
                    accommodation: updatedHotelPassengers,
                }),
                //handle domestic hotel booking in the itinerary flow when the flight is international and the hotel is not international
                ...(!isHotelInternational && selectedHotel?.tag === "pr" && domestic_pr_accommodation.push(updatedHotelPassengers)),
                ...(!isHotelInternational &&
                    selectedHotel?.tag === "grs" &&
                    domestic_grs_accommodation.push({
                        id: selectedHotel?.id,
                        check_in: hotelInformation?.checkIn,
                        check_out: hotelInformation?.checkOut,
                        rooms: updatedHotelPassengers,
                    })),

                ...(selectedIntFlight.tag === "pr" && { flight }),
                ...(selectedIntFlight.tag === "ch118" && { ch118_flight }),
            };
            console.log(requestData);

            try {
                setLoading(true);
                const response = await axios.post(`${BASE_URL_IR}/api/v2/reserve/foreign/order/`, requestData, {
                    params: {
                        wallet: isPaymentByCredit,
                    },
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                });
                if (isPaymentByCredit && !response.data.data.opg) {
                    handleDirectWalletPayment(response.data.data);
                } else {
                    window.location.replace(`${response.data.data.opg}`);
                }
                setLoading(false);
            } catch (error) {
                handleError(error);
                setLoading(false);
            }
        } else {
            setSearchParams({ action: "login" });
        }
    };
    // Handle direct wallet payment without gateway for zero-amount transactions
    const handleDirectWalletPayment = async paymentResult => {
        let paymentResponse;
        try {
            setLoading(true);
            const paymentData = {
                wallet: true,
                total_price: paymentResult?.total_price,
                tracking_code: paymentResult?.tracking_code,
            };
            paymentResponse = await axios.post(`${BASE_URL}/api/v2/reserve/save_payment_gateway_result/`, paymentData);
        } catch (error) {
            console.error("Payment skip process failed:", error);
        } finally {
            setLoading(false);
            navigate({
                pathname: `/${locale}/payment-result`,
                search: createSearchParams({
                    pk: paymentResponse.data.data.pk,
                    wallet: paymentResponse.data.data.wallet,
                }).toString(),
            });
        }
    };

    return {
        internationalBooking,
    };
};
export default useInternationalBooking;
